import { HelpCircle, Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { TaskConfig, CatchRule } from '../types';
import { ERROR_TYPES } from '../types';

interface ErrorsTabProps {
    config: TaskConfig;
    availableNodes: string[];
    onAddCatch: () => void;
    onUpdateCatch: (id: string, updates: Partial<CatchRule>) => void;
    onRemoveCatch: (id: string) => void;
    validationErrors: Record<string, string>;
    readOnly: boolean;
}

export function ErrorsTab({
    config,
    availableNodes,
    onAddCatch,
    onUpdateCatch,
    onRemoveCatch,
    validationErrors,
    readOnly,
}: ErrorsTabProps) {
    return (
        <div className="p-6 space-y-8">
            {/* Error Routing Section */}
            <section className="space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Label className="text-base font-semibold">Error Routing</Label>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <HelpCircle size={14} className="text-slate-400" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                    <p>
                                        Configure how errors are handled after retries are exhausted.
                                        Route different error types to different error handler states.
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>

                {/* Catch Rules List */}
                <div className="space-y-4">
                    {config.catch.map((rule, index) => {
                        const hasError = validationErrors[`catch.${index}`];

                        return (
                            <div
                                key={rule.id}
                                className={cn(
                                    "rounded-lg border p-4 space-y-4",
                                    hasError ? "border-red-300 bg-red-50/50" : "border-slate-200"
                                )}
                            >
                                {/* Header */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-slate-900">
                                            Catch Rule #{index + 1}
                                        </span>
                                    </div>
                                    {!readOnly && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onRemoveCatch(rule.id)}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    )}
                                </div>

                                {/* Error Types */}
                                <div className="space-y-2">
                                    <Label className="text-sm">Error Types to Catch</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {ERROR_TYPES.map((errorType) => (
                                            <label
                                                key={errorType.key}
                                                className="flex items-center gap-2 text-sm"
                                            >
                                                <Checkbox
                                                    checked={rule.errorTypes.includes(errorType.key)}
                                                    onCheckedChange={(checked) => {
                                                        const newTypes = checked
                                                            ? [...rule.errorTypes, errorType.key]
                                                            : rule.errorTypes.filter((t) => t !== errorType.key);
                                                        onUpdateCatch(rule.id, {
                                                            errorTypes: newTypes.length > 0 ? newTypes : ['States.ALL'],
                                                        });
                                                    }}
                                                    disabled={readOnly}
                                                />
                                                <span>{errorType.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Target Node */}
                                <div className="space-y-2">
                                    <Label className="text-sm">Route to State</Label>
                                    {availableNodes.length > 0 ? (
                                        <Select
                                            value={rule.targetNode}
                                            onValueChange={(value) =>
                                                onUpdateCatch(rule.id, { targetNode: value })
                                            }
                                            disabled={readOnly}
                                        >
                                            <SelectTrigger className={cn(hasError && "border-red-300")}>
                                                <SelectValue placeholder="Select target state" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {availableNodes.map((node) => (
                                                    <SelectItem key={node} value={node}>
                                                        {node}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <Input
                                            type="text"
                                            value={rule.targetNode}
                                            onChange={(e) =>
                                                onUpdateCatch(rule.id, { targetNode: e.target.value })
                                            }
                                            placeholder="Enter state name"
                                            className={cn("font-mono", hasError && "border-red-300")}
                                            disabled={readOnly}
                                        />
                                    )}
                                    {hasError && (
                                        <p className="text-xs text-red-600">{hasError}</p>
                                    )}
                                </div>

                                {/* Result Path */}
                                <div className="space-y-2">
                                    <Label className="text-sm">Store Error At (optional)</Label>
                                    <Input
                                        type="text"
                                        value={rule.resultPath || ''}
                                        onChange={(e) =>
                                            onUpdateCatch(rule.id, { resultPath: e.target.value || undefined })
                                        }
                                        placeholder="$.error"
                                        className="font-mono"
                                        disabled={readOnly}
                                    />
                                    <p className="text-xs text-slate-500">
                                        JSONPath where error details are stored in state.
                                    </p>
                                </div>
                            </div>
                        );
                    })}

                    {config.catch.length === 0 && (
                        <div className="text-center py-6 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                            <p>No catch rules configured.</p>
                            <p className="text-sm mt-1">
                                Unhandled errors will cause the workflow to fail.
                            </p>
                        </div>
                    )}

                    {!readOnly && (
                        <Button
                            variant="outline"
                            onClick={onAddCatch}
                            className="w-full"
                        >
                            <Plus size={16} className="mr-2" />
                            Add Catch Rule
                        </Button>
                    )}
                </div>
            </section>

            {/* Error Behavior Info */}
            <section className="space-y-3">
                <Label className="text-base font-semibold">Error Handling Order</Label>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                    <div className="flex items-start gap-3">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                            1
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-900">Retry Policy Applied</p>
                            <p className="text-xs text-slate-500">
                                Matching retry rules are executed first.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-600 text-sm font-medium">
                            2
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-900">Catch Rules Evaluated</p>
                            <p className="text-xs text-slate-500">
                                After retries exhaust, catch rules are checked in order.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-600 text-sm font-medium">
                            3
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-900">Workflow Failure</p>
                            <p className="text-xs text-slate-500">
                                If no catch rule matches, the workflow fails with the error.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

